// Index = outcome count; mass 1 ± PMF_MASS_TOLERANCE, enforced in zod where PMFs enter.
export type Pmf = readonly number[];

export const PMF_MASS_TOLERANCE = 1e-9;

export function meanFromPmf(pmf: Pmf): number {
  return pmf.reduce((mean, p, k) => mean + k * p, 0);
}

const TILT_TOLERANCE = 1e-12;
const TILT_MAX_ITERATIONS = 200;
const BOUNDARY_TOLERANCE = 1e-9;

// Reweights bin k by e^{λk}; exponents are shifted by the largest so nothing overflows.
export function exponentialTilt(pmf: Pmf, lambda: number): number[] {
  let maxExponent = -Infinity;
  for (let k = 0; k < pmf.length; k++) if (pmf[k] > 0) maxExponent = Math.max(maxExponent, lambda * k);
  const weights = pmf.map((p, k) => (p > 0 ? p * Math.exp(lambda * k - maxExponent) : 0));
  const z = weights.reduce((a, b) => a + b, 0);
  return weights.map(w => w / z);
}

// Expands a bracket, then bisects. `objective` must increase with λ, so failing to converge is a
// programming error, not a data condition.
export function solveTilt(weights: Pmf, objective: (tilted: number[]) => number, target: number): number[] {
  const at = (lambda: number) => objective(exponentialTilt(weights, lambda));
  let lo = -1;
  let hi = 1;
  // A target past the support never brackets: λ doubles to ±Infinity, where the tilt is NaN.
  while (Number.isFinite(lo) && at(lo) > target) lo *= 2;
  while (Number.isFinite(hi) && at(hi) < target) hi *= 2;
  let lambda = 0;
  let value = at(lambda);
  for (let i = 0; i < TILT_MAX_ITERATIONS && Math.abs(value - target) > TILT_TOLERANCE; i++) {
    if (value < target) lo = lambda; else hi = lambda;
    lambda = (lo + hi) / 2;
    value = at(lambda);
  }
  if (!(Math.abs(value - target) <= TILT_TOLERANCE)) throw new RangeError(`tilt did not converge: ${value} vs ${target}`);
  return exponentialTilt(weights, lambda);
}

// A mean on the support boundary is that bin alone; outside the support no tilt reaches it.
export function tiltToMean(pmf: Pmf, target: number): number[] {
  const support = pmf.flatMap((p, k) => (p > 0 ? [k] : []));
  const min = support[0];
  const max = support[support.length - 1];
  if (!(target >= min - BOUNDARY_TOLERANCE && target <= max + BOUNDARY_TOLERANCE)) {
    throw new RangeError(`target mean ${target} is outside the support [${min}, ${max}]`);
  }
  const boundary = Math.abs(target - min) <= BOUNDARY_TOLERANCE ? min
    : Math.abs(target - max) <= BOUNDARY_TOLERANCE ? max : null;
  if (boundary !== null) return pmf.map((_, k) => (k === boundary ? 1 : 0));
  return solveTilt(pmf, meanFromPmf, target);
}
